import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { authService } from '@features/authentication/services/auth.service';
import type { LoginCredentials, User } from '@features/authentication/types';
import { APP_ROUTES } from '@shared/constants/routes.constants';

/**
 * Hook for login mutation
 * Handles login process and redirects based on user status
 */
export const useLogin = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: LoginCredentials) => authService.login(credentials),
    onSuccess: async () => {
      try {
        // Fetch user data to check first access status
        const user = await authService.getCurrentUser();

        // Update cache with user data
        queryClient.setQueryData(['currentUser'], user);

        // Redirect based on first access status
        if (user.isFirstAccess) {
          void navigate(APP_ROUTES.CREATE_PASSWORD, { replace: true });
        } else {
          void navigate(APP_ROUTES.MODULES, { replace: true });
        }
      } catch (error) {
        // If we can't fetch user, redirect to modules anyway
        console.error('Failed to fetch user after login:', error);
        void navigate(APP_ROUTES.MODULES, { replace: true });
      }
    },
    onError: (error) => {
      console.error('Login failed:', error);
    },
  });
};

/**
 * Hook for logout mutation
 * Handles logout process and redirects to login
 */
export const useLogout = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      // Clear all React Query cache
      queryClient.clear();

      // Redirect to login
      void navigate(APP_ROUTES.LOGIN);
    },
  });
};

/**
 * Hook to get current authenticated user
 * Only runs if user is authenticated
 */
export const useCurrentUser = () => {
  return useQuery<User>({
    queryKey: ['currentUser'],
    queryFn: () => authService.getCurrentUser(),
    enabled: authService.isAuthenticated(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
};

/**
 * Hook to check if user is authenticated
 */
export const useIsAuthenticated = (): boolean => {
  return authService.isAuthenticated();
};
